import { useState } from 'react';

import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { Loading } from 'components/Loading';
import ShowMore from 'components/ShowMore';
import { Modal, ModalProps } from 'features/modals';
import { IApplication } from 'models/types';

type Props = ModalProps<{
  applicants: IApplication[];
}>;

export const BulkActiveApplicantsModal = observer((props: Props) => {
  const { t } = useScopedTranslate(locale);
  const { applicants, closeModal } = props;
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit() {
    const job = applicants[0].job;
    setSubmitting(true);

    const success = await job.bulkActiveApplicants(applicants);

    if (success) {
      closeModal();
    } else {
      setSubmitting(false);
    }
  }

  const disableButtons = submitting;
  return (
    <Modal>
      <Modal.Header text={t('makeActive')} />
      {submitting && <Loading />}
      {applicants.length === 0 && (
        <>
          <Text text={t('noUnActiveApplicants')} />
          <Modal.Actions>
            <Button variant="secondary" onClick={closeModal} text={t('close')} />
          </Modal.Actions>
        </>
      )}
      {applicants.length > 0 && (
        <>
          <Stack space={3}>
            <Text text={t('areYouSure', { number: applicants.length })} />
            <Text text={t('info')} font="regular14" />
            <ShowMore
              max={applicants.length}
              count={applicants.length > 15 ? 10 : applicants.length}
              inc={1000}
              render={({ count }: { count: number }) => (
                <div>
                  {applicants.slice(0, count).map((a, i) => (
                    <Text key={i} font="bold16" color="purple100" text={a.name} />
                  ))}
                </div>
              )}
            />
          </Stack>
          <Modal.Actions>
            <Button
              isDisabled={submitting}
              variant="secondary"
              onClick={closeModal}
              text={t('cancel')}
            />
            <Button isDisabled={disableButtons} onClick={onSubmit} text={t('submit')} />
          </Modal.Actions>
        </>
      )}
    </Modal>
  );
});

const locale = {
  en: {
    makeActive: 'Make applications active',
    noUnActiveApplicants: 'There are no applicants that are not active in the selection',
    close: 'Close',
    areYouSure: 'Are you sure you wish to make these {number} applications active?',
    info: 'This will change the application status from "Hired", "Retracted" and "Rejected" to "Active".',
    cancel: 'Cancel',
    submit: 'Submit',
  },
  is: {
    makeActive: 'Gera umsóknir virkar',
    noUnActiveApplicants: 'Það eru engar umsóknir sem eru ekki virkar í valinu',
    close: 'Loka',
    areYouSure: 'Ertu viss um að þú viljir gera þessar {number} umsóknir virkar?',
    info: 'Þetta mun breyta umsóknunum úr stöðunni "Ráðin/n", "Dregin til baka" og "Hafnað" yfir í "Virk".',
    cancel: 'Hætta við',
    submit: 'Staðfesta',
  },
};
